/**
 * Typography constants — font presets and title sizing.
 * Extracted from GlassCard.js for clean dependency direction.
 *
 * Session 37: Added accent colors per preset for instrument-style buttons.
 */

/** Title font size levels */
export const TITLE_FONT_SIZE_LEVELS = [
  { value: 0, label: 'XS', size: 48 },
  { value: 1, label: 'Small', size: 56 },
  { value: 2, label: 'Medium', size: 64 },
  { value: 3, label: 'Large', size: 74 },
  { value: 4, label: 'XL', size: 84 },
  { value: 5, label: 'XXL', size: 96 },
];

export const DEFAULT_TITLE_FONT_SIZE = 3; // Large (74px - current default)

/** Body font size levels */
export const BODY_FONT_SIZE_LEVELS = [
  { value: 0, label: 'XS', size: 20 },
  { value: 1, label: 'Small', size: 24 },
  { value: 2, label: 'Medium', size: 28 },
  { value: 3, label: 'Large', size: 32 },
  { value: 4, label: 'XL', size: 36 },
  { value: 5, label: 'XXL', size: 40 },
];

export const DEFAULT_BODY_FONT_SIZE = 3; // Large (32px - current default)

/** Kicker font size levels */
export const KICKER_FONT_SIZE_LEVELS = [
  { value: 0, label: 'XS', size: 10 },
  { value: 1, label: 'Small', size: 12 },
  { value: 2, label: 'Medium', size: 14 },
  { value: 3, label: 'Large', size: 16 },
  { value: 4, label: 'XL', size: 18 },
  { value: 5, label: 'XXL', size: 22 },
];

export const DEFAULT_KICKER_FONT_SIZE = 3; // Large (16px)

/** Subtitle font size levels */
export const SUBTITLE_FONT_SIZE_LEVELS = [
  { value: 0, label: 'XS', size: 14 },
  { value: 1, label: 'Small', size: 17 },
  { value: 2, label: 'Medium', size: 20 },
  { value: 3, label: 'Large', size: 24 },
  { value: 4, label: 'XL', size: 28 },
  { value: 5, label: 'XXL', size: 32 },
];

export const DEFAULT_SUBTITLE_FONT_SIZE = 2; // Medium (20px)

/** Stats font size levels (controls stat label; stat value scales proportionally) */
export const STATS_FONT_SIZE_LEVELS = [
  { value: 0, label: 'XS', size: 14 },
  { value: 1, label: 'Small', size: 16 },
  { value: 2, label: 'Medium', size: 18 },
  { value: 3, label: 'Large', size: 20 },
  { value: 4, label: 'XL', size: 24 },
  { value: 5, label: 'XXL', size: 28 },
];

export const DEFAULT_STATS_FONT_SIZE = 3; // Large (20px — matches current)

/** Tags font size levels */
export const TAGS_FONT_SIZE_LEVELS = [
  { value: 0, label: 'XS', size: 9 },
  { value: 1, label: 'Small', size: 10 },
  { value: 2, label: 'Medium', size: 11 },
  { value: 3, label: 'Large', size: 12 },
  { value: 4, label: 'XL', size: 14 },
  { value: 5, label: 'XXL', size: 16 },
];

export const DEFAULT_TAGS_FONT_SIZE = 2; // Medium (11px)

/** Font presets — each defines title, subtitle, body font families + accent color */
export const FONT_PRESETS = [
  {
    id: 0,
    name: 'Classic',
    title: "'Inter', sans-serif",
    subtitle: "'Inter', sans-serif",
    body: "'Inter', sans-serif",
    titleWeight: 600,
    titlePreview: 'Inter',
    accent: '#00d4ff',
    accentGlow: 'rgba(0, 212, 255, 0.25)',
  },
  {
    id: 1,
    name: 'Elegant',
    title: "'Raleway', sans-serif",
    subtitle: "'Rosario', sans-serif",
    body: "'Mukta Vaani', sans-serif",
    titleWeight: 700,
    titlePreview: 'Raleway',
    accent: '#b026ff',
    accentGlow: 'rgba(176, 38, 255, 0.25)',
  },
  {
    id: 2,
    name: 'Modern',
    title: "'Montserrat', sans-serif",
    subtitle: "'Catamaran', sans-serif",
    body: "'Noto Sans', sans-serif",
    titleWeight: 700,
    titlePreview: 'Montserrat',
    accent: '#ff007f',
    accentGlow: 'rgba(255, 0, 127, 0.25)',
  },
  {
    id: 3,
    name: 'Editorial',
    title: "'Playfair Display', serif",
    subtitle: "'Source Sans 3', sans-serif",
    body: "'Source Sans 3', sans-serif",
    titleWeight: 700,
    titlePreview: 'Playfair',
    accent: '#ffd700',
    accentGlow: 'rgba(255, 215, 0, 0.25)',
  },
  {
    id: 4,
    name: 'Minimal',
    title: "'DM Sans', sans-serif",
    subtitle: "'DM Sans', sans-serif",
    body: "'DM Sans', sans-serif",
    titleWeight: 600,
    titlePreview: 'DM Sans',
    accent: '#32cd32',
    accentGlow: 'rgba(50, 205, 50, 0.25)',
  },
  {
    id: 5,
    name: 'Luxe',
    title: "'Cormorant Garamond', serif",
    subtitle: "'Lato', sans-serif",
    body: "'Lato', sans-serif",
    titleWeight: 600,
    titlePreview: 'Cormorant',
    accent: '#ff6b99',
    accentGlow: 'rgba(255, 107, 153, 0.25)',
  },
  {
    id: 6,
    name: 'Brutalist',
    title: "'Space Mono', monospace",
    subtitle: "'Space Grotesk', sans-serif",
    body: "'Space Grotesk', sans-serif",
    titleWeight: 700,
    titlePreview: 'Space Mono',
    accent: '#ff6b2b',
    accentGlow: 'rgba(255, 107, 43, 0.25)',
  },
  {
    id: 7,
    name: 'Friendly',
    title: "'Poppins', sans-serif",
    subtitle: "'Poppins', sans-serif",
    body: "'Poppins', sans-serif",
    titleWeight: 600,
    titlePreview: 'Poppins',
    accent: '#00e5a0',
    accentGlow: 'rgba(0, 229, 160, 0.25)',
  },
  {
    id: 8,
    name: 'Impact',
    title: "'Bebas Neue', sans-serif",
    subtitle: "'Inter', sans-serif",
    body: "'Inter', sans-serif",
    titleWeight: 400,
    titlePreview: 'Bebas Neue',
    accent: '#ff3333',
    accentGlow: 'rgba(255, 51, 51, 0.25)',
  },
];

export const DEFAULT_FONT_PRESET = 0; // Classic (Inter)
